import { Button, IconSet } from '@postscript/components';
import CardPreview from 'components/messagePreview/components/CardPreview';
import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { RcsMessageCard } from 'types/rcs';

const CarouselWrapper = styled.div`
  position: relative;
`;

interface CarouselViewportProps {
  overflowSize?: string;
}

const CarouselViewport = styled.div<CarouselViewportProps>`
  left: ${({ overflowSize }) => `calc(${overflowSize} * -1)`};
  overflow: hidden;
  padding: 0 ${({ overflowSize }) => overflowSize};
  position: relative;
  width: calc(100% + calc(${({ overflowSize }) => overflowSize} * 2));
`;

const CarouselContainer = styled.div`
  display: flex;
  gap: var(--spacing-2);
`;

const CarouselSlide = styled.div`
  flex: 0 0 100%;
  min-width: 0;
`;

const CarouselButtonWrapper = styled.div`
  background-color: var(--surface-bkg-color);
  border-radius: 50%;
  height: var(--spacing-4);
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
`;

const CarouselButtonWrapperPrev = styled(CarouselButtonWrapper)`
  left: calc(var(--spacing-3) * -1);
`;

const CarouselButtonWrapperNext = styled(CarouselButtonWrapper)`
  right: calc(var(--spacing-3) * -1);
`;

interface CarouselPreviewProps {
  cards: RcsMessageCard[];
  overflowSize?: string;
}

const CarouselPreview = ({
  cards,
  overflowSize = 'var(--spacing-4)',
}: CarouselPreviewProps) => {
  const isTouchDevice =
    'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    watchDrag: isTouchDevice,
  });

  const [, setScrollSnaps] = useState<number[]>([]);
  const [, setSelectedIndex] = useState(0);

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  return (
    <CarouselWrapper>
      <CarouselViewport ref={emblaRef} overflowSize={overflowSize}>
        <CarouselContainer>
          {cards.map((card, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <CarouselSlide key={index}>
              <CardPreview card={card} />
            </CarouselSlide>
          ))}
        </CarouselContainer>
      </CarouselViewport>
      <CarouselButtonWrapperPrev onClick={(e) => e.stopPropagation()}>
        <Button
          aria-label="Go to previous Card"
          disabled={!emblaApi?.canScrollPrev()}
          icon={IconSet.ArrowLeft}
          onClick={() => emblaApi?.scrollPrev()}
          size="small"
          variant="secondary"
        />
      </CarouselButtonWrapperPrev>
      <CarouselButtonWrapperNext onClick={(e) => e.stopPropagation()}>
        <Button
          aria-label="Go to next Card"
          disabled={!emblaApi?.canScrollNext()}
          icon={IconSet.ArrowRight}
          onClick={() => emblaApi?.scrollNext()}
          size="small"
          variant="secondary"
        />
      </CarouselButtonWrapperNext>
    </CarouselWrapper>
  );
};

export default CarouselPreview;
